import { inject } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { QuoteService } from 'pages/quotes/service';
import { DialogService } from 'aurelia-dialog';
import { formatTableLink, createButton, createTableSelectInput, formatTableDate } from 'resources/utility';
import { Router } from 'aurelia-router';
import toastr from 'toastr';
import { delegate } from 'tippy.js';

@inject(QuoteService, DialogService, EventAggregator, Router)
export class Quotes {
    constructor(quoteService, dialogService, ea, router) {
        this.ea = ea;
        this.quoteService = quoteService;
        this.dialogService = dialogService;
        this.router = router;
        this.items = [{}];
        this.printQuoteIds = [];

        this.columns = [
            { dataField: 'partNumber', sortOrder: 'asc', caption: 'Part Number', cellTemplate: (container, options) => formatTableLink({ container, containerOptions: options, callback: this.edit }) },
            { dataField: 'plantName', caption: 'Plant' },
            { dataField: 'customerName', caption: 'Customer'},
            { dataField: 'projectNumber' },
            { dataField: 'id', caption: 'QS#' },
            { dataField: 'total', allowFiltering: false, allowSorting: false, allowSearch: false, format: { type: 'fixedPoint', precision: 4 } },
            { caption: 'Actions', cellTemplate: (container, options) => {
              const deleteButton = createButton({ container, containerOptions: options, style: 'danger', label: 'Delete', action: this.delete });
              deleteButton.style.marginRight = '20px';
              const cloneButton = createButton({ container, containerOptions: options, style: 'primary', label: 'Clone', action: this.clone });

              const div = document.createElement('div');
              div.appendChild(deleteButton);
              div.appendChild(cloneButton);
              container.append(div);
          }, allowExporting: false },
          { caption: 'Selected', width: '110px', headerCellTemplate: (container, options) => {
                    var selectAllBtn = createButton({ container, containerOptions: options, style: 'primary btn-sm d-flex p-1', label: '<i class="bx bx-check-square bx-xs"></i>', action: () => this.selectAll() }) 
                    selectAllBtn.style.marginRight = '10px';
                    var unselectAllBtn = createButton({ container, containerOptions: options, style: 'primary btn-sm d-flex p-1', label: '<i class="bx bx-checkbox bx-xs"></i>', action: () => this.clearAll() }) 

                    const div = document.createElement('div');
                    div.classList.add('d-flex');
                    div.appendChild(selectAllBtn);
                    div.appendChild(unselectAllBtn);
                    container.append(div);
                }, cellTemplate: (container, options) => {
                let isSelected = false;
                if (this.printQuoteIds.includes(options.data.id)) {
                    isSelected = true;
                }

                return createTableSelectInput({ container, containerOptions: options, action: this.link, isSelected });
            }},
        ];

        this.subscription = this.ea.subscribe('quotes:updated', () => {
            this.grid.refresh();
        });
    }

    attached() {
      delegate('body', {
        target: '.snapshot-description',
        content(reference) {
            const message = reference.getAttribute('data-message');
            return message;
        }
      });
    }

    link = (options, event) => {
        if (event.target.checked) {
            this.printQuoteIds.push(options.data.id);
        } else {
            const index = this.printQuoteIds.indexOf(options.data.id);
            if (index != -1) {
                this.printQuoteIds.splice(index, 1);
            }
        }
    };

    selectAll = async () => {
        const filter = this.grid.getFilter();
        const quoteIds = await this.quoteService.getIdsByFilter(filter);
        this.printQuoteIds = quoteIds.map(x => x.id);
        this.grid.refresh();
    };

    clearAll = () => {
        this.printQuoteIds = [];
        this.grid.refresh();
    };

    deactivate () {
        this.subscription.dispose();
    }

    edit = (data) => {
      this.router.navigate(`quote/edit/${data.id}`);
    };

    add = () => {
        this.dialogService.open({ viewModel: this.quoteService.add });
    };

    clone = (item) => {
      this.dialogService.open({ viewModel: this.quoteService.add, model: { ...item, isClone: true } });
    };

    delete = (item) => {
      this.dialogService.open({ viewModel: PLATFORM.moduleName('resources/elements/modals/confirm/index') }).whenClosed(async response => {
          if (!response.wasCancelled) {
              await this.quoteService.delete(item.id);
              this.grid.refresh();
              toastr.success('Quote was deleted!');
          }
      });
    };

    print = () => {
        if (this.printQuoteIds.length == 0) {
            toastr.error('No quotes selected to print');
            return;
        }
        
        window.localStorage.setItem('printFilter', JSON.stringify(this.printQuoteIds));
        this.printQuoteIds = [];
        this.grid.refresh();
        window.open(`quote/print?print=true`);
    }

    bulkSnapshot = () => {
      this.dialogService.open({ viewModel: PLATFORM.moduleName('pages/quotes/bulk-snapshot/index') }).whenClosed(async response => {
        if (!response.wasCancelled) {
          this.isSnapshotting = true;
          try {
            await this.quoteService.bulkSnapshot(response.output);
            this.grid.refresh();
            toastr.success('Bulk snapshot was created!');
          } catch (error) {
            toastr.error('Error creating bulk snapshot');
          } finally {
            this.isSnapshotting = false;
          }
        }
      });
    }

    generateSnapshotMasterDetail() {
        return {
            enabled: true,
            autoExpandAll: false,
            template: (container, options) => {
                console.log(options);
                $('<h6>')
                    .addClass('master-detail-caption')
                    .text(`Snapshots`)
                    .appendTo(container);
                const gridContainer = $('<div>');
                gridContainer
                    .dxDataGrid({
                        columnAutoWidth: true,
                        showBorders: true,
                        columns: [
                              { dataField: 'description', cellTemplate: (container, options) => {
                                const info = document.createElement('i');
                                info.classList.add('bx', 'bx-info-circle', 'bx-sm', 'snapshot-description');
                                info.setAttribute('data-message', options.data.description);
                                container.append(info);
                              }},
                              { dataField: 'snapshotType', cellTemplate: (container, options) => formatTableLink({ container, containerOptions: options, callback: (data) => {
                                window.open(`quote/edit/${data.quoteId}?snapshotId=${data.id}`, '_blank');
                              } }) },
                              { dataField: 'productionType' },
                              { dataField: 'effectiveDate', customizeText: formatTableDate },
                              { dataField: 'poNumber' },
                              { dataField: 'revision' },
                              { dataField: 'createdAt', customizeText: formatTableDate, sortOrder: 'desc' },
                              { dataField: 'createdBy' },
                              { caption: 'Actions', cellTemplate: (container, options) => {
                                const div = document.createElement('div');
                                const deleteButton = createButton({ container, containerOptions: options, style: 'danger', label: 'Delete', action: () => this.deleteSnapshot(options.data, gridContainer.dxDataGrid('instance')) });
                                deleteButton.style.marginRight = '20px';
                                div.appendChild(deleteButton);
                                const editButton = createButton({ container, containerOptions: options, style: 'primary', label: 'Edit', action: () => this.editSnapshot(options.data, gridContainer.dxDataGrid('instance')) });
                        
                                div.appendChild(editButton);
                                container.append(div);
                              }, allowExporting: false }
                        ],
                        dataSource: new DevExpress.data.DataSource({
                            load: async (loadOptions) => await this.quoteService.filterSnapshots(JSON.stringify(loadOptions), [options.data.id, {}])
                        }),
                        onContentReady: (e) => {
                            e.element.find(".dx-datagrid-text-content").removeClass("dx-text-content-alignment-left");
                        },
                        onCellPrepared: (e) => {
                        }
                    }).appendTo(container);
            }
        };
    }

    editSnapshot = (item, grid) => {
        this.dialogService.open({
          viewModel: PLATFORM.moduleName('pages/quotes/edit-snapshot/index'),
          model: { quoteId: item.quoteId, snapshot: item }
        }).whenClosed(async result => {
          if (!result.wasCancelled) {
            await this.quoteService.updateSnapshot(item.quoteId, result.output);
            grid.refresh();
          }
        });
      }
  
      deleteSnapshot = (item, grid) => {
        this.dialogService.open({
          viewModel: PLATFORM.moduleName('resources/elements/modals/confirm/index'),
          model: { message: 'Are you sure you want to delete this snapshot?' }
        }).whenClosed(async result => {
          if (!result.wasCancelled) {
            await this.quoteService.deleteSnapshot(item.quoteId, item.id);
            grid.refresh();
          }
        });
      }
}
